import React, { useState } from 'react';
import { IProject } from 'app/services/responseTypes';
import ProgressBar, { TaxReturnStatuses } from 'Components/ProgressBar';
import moment from 'moment';

export function ProjectStatusBadge({ project }: { project: IProject }) {
  if (project.status === 'draft') {
    return (
      <div className="flex">
        <div className="px-2 bg-gray-750 text-[#696969] text-sm font-semibold rounded-md flex items-center">
          Draft
        </div>
      </div>
    );
  }
  if (project.status === 'pending_pricing') {
    return (
      <div className="flex">
        <div className="px-2 bg-gray-750 text-[#696969] text-sm font-semibold rounded-md flex items-center">
          Price Pending
        </div>
      </div>
    );
  }
  if (project.status === 'pending_payment') {
    return (
      <div className="flex gap-x-3.5">
        <div className="text-green text-regular font-semibold">
          ${project.price.paid_cents / 100}
        </div>
        <div className="px-2 bg-red-light text-red-dark text-sm font-semibold rounded-md flex items-center">
          Unpaid
        </div>
      </div>
    );
  }
  if (project.status === 'approved' || project.status === 'assigned') {
    return (
      <div className="flex gap-x-3.5">
        <div className="text-green text-regular font-semibold">
          ${project.price.amount_cents / 100}
        </div>
        <div className="px-2 bg-green-20 text-green text-sm font-semibold rounded-md flex items-center">
          Paid
        </div>
      </div>
    );
  }
  if (project.status === 'rejected') {
    return (
      <div className="flex">
        <div className="px-2 bg-red-100 text-red-dark text-sm font-semibold rounded-md flex items-center">
          Rejected
        </div>
      </div>
    );
  }
  if (project.status === 'cancelled') {
    return (
      <div className="flex">
        <div className="px-2 bg-red-100 text-red-dark text-sm font-semibold rounded-md flex items-center">
          Cancelled
        </div>
      </div>
    );
  }
  return null;
}

export default function ProjectCard({
  project,
  onClick,
  children,
}: {
  project: IProject;
  onClick?: () => void;
  children?: React.ReactNode;
}) {
  const [fallbackVisible, setFallbackVisible] = useState(false);

  // Function to open both the engagement letter and the survey in separate tabs.
  const openEngagementAndSurvey = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sign_url: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const engagementTab = window.open(
      sign_url,
      '_blank',
      'noopener,noreferrer',
    );
    if (!engagementTab) {
      setFallbackVisible(true);
      return;
    }

    setTimeout(() => {
      const surveyTab = window.open(
        'https://us16.list-manage.com/survey?u=74a0e04a8fc87374ff7e06f81&id=4d1daeedb0&attribution=false',
        '_blank',
        'noopener,noreferrer',
      );
      if (!surveyTab) {
        setFallbackVisible(true);
      }
    }, 100);
  };

  return (
    <div
      className={`${
        onClick ? 'cursor-pointer hover:bg-gray-400 hover:bg-opacity-10' : ''
      } shadow-all px-6 py-5 rounded-2xl flex-wrap bg-white flex items-center md-max:px-4`}
      onClick={onClick}
    >
      <div className="flex justify-start flex-grow">
        <div className="w-48 flex flex-col justify-between md-max:w-min md-max:py-4">
          <div>
            <div className="text-regular w-full font-extrabold md-max:flex-1 text-gray-dark overflow-hidden">
              <span>{project.title}</span>
            </div>
            <div className="text-sm text-gray-light md-max:w-16 whitespace-nowrap ">
              {moment(project.created_at).format('MM/DD/YYYY')}
            </div>
          </div>
          <div className="mt-2">
            <ProjectStatusBadge project={project} />
          </div>
        </div>

        <div className="border-r ml-2 mr-8 md-max:hidden self-stretch"></div>

        <div className="flex-grow flex flex-col gap-y-3.5">
          <ProgressBar
            className="min-w-max md-max:hidden"
            labelPosition="bottom"
            status={
              project.tax_return_status
                ? project.tax_return_status
                : project.status === 'draft'
                ? 'draft'
                : 'submitted'
            }
            statuses={TaxReturnStatuses}
          />
          <div className="text-green text-base capitalize font-medium w-24 text-center lg:hidden">
            {project.tax_return_status}
          </div>

          {['approved', 'assigned', 'pending_payment'].includes(
            project.status,
          ) &&
            project.engagement_letter?.sign_url && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="w-fit"
              >
                <a
                  href="#"
                  onClick={(e) =>
                    openEngagementAndSurvey(
                      e,
                      project.engagement_letter.sign_url,
                    )
                  }
                  className="text-xs font-medium text-green underline"
                  rel="noreferrer"
                >
                  Sign Engagement Letter
                </a>
              </div>
            )}

          <div onClick={(e) => e.stopPropagation()} className="w-fit">
            {project.engagement_letter?.signing_status === 'signed' &&
              project.engagement_letter?.preview_url && (
                <a
                  href={project.engagement_letter.preview_url}
                  className="text-xs font-medium text-green underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  Preview Engagement Letter
                </a>
              )}
          </div>

          {fallbackVisible && (
            <div className="mt-2 p-2 bg-red-100 text-red-700 rounded text-xs">
              It looks like pop-ups are blocked. Please click the links below
              manually:
              <ul className="list-disc ml-4">
                <li>
                  <a
                    href={project.engagement_letter?.sign_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-blue-600"
                  >
                    Engagement Letter
                  </a>
                </li>
                <li>
                  <a
                    href="https://us16.list-manage.com/survey?u=74a0e04a8fc87374ff7e06f81&id=4d1daeedb0&attribution=false"
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-blue-600"
                  >
                    Survey
                  </a>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="ml-11 flex justify-end md-max:ml-0 md-max:mt-0 lg-max:mt-2">
        {children}
      </div>
    </div>
  );
}
